import axios from 'axios';
import qs from 'qs';
import FormData from 'form-data';
import { Indicator, Toast, MessageBox } from 'ym-mint-ui';
import store from '@/store';
import router from '@/router';
import {
  monitor, setMonitorUser, getUID, locationHref, getAuth, setAuth, isPC,
} from '@/utils';
import validator from '@/utils/validator';
import { changePageShareInfo } from '@/utils/share';
import {
  TOKEN_NAME,
  API_REQUEST_PREFIX,
  PLATFORM_API_KEY,
  PC_PLATFORM_CODE,
  H5_PLATFORM_CODE,
} from '@/utils/constant';

/**
 * 是否外链请求
 * @param options
 */
function isExternalRequest(options) {
  return options.baseUrl;
}

/**
 * 当前platform
 */
function platformName() {
  return isPC() ? PC_PLATFORM_CODE : H5_PLATFORM_CODE;
}

/**
 * 展示提示信息
 * @param message
 */
function showMessage(message) {
  if (message == null || message === '') return;
  Toast({
    message,
  });
}

/**
 * 格式化数据
 * @param data
 */
function stringifyData(data) {
  return qs.stringify(data, { arrayFormat: 'indices' });
}

/**
 * 处理跳转
 * @param message
 * @param location
 */
function handleRedirect(message, location) {
  if (message && message !== '') {
    MessageBox.alert(message, '', {
      confirmButtonText: '知道了',
      closeOnClickModal: false,
    }).then(() => {
      locationHref(location);
    });
    return;
  }
  locationHref(location);
}

/**
 * 跳转至登录页
 */
function redirectToLoginPage() {
  const { fullPath } = router.history.current;
  const targetHref = `${process.env.VUE_APP_OFFICIAL_WEBSITE_DOMAIN}/login`;
  const currentPath = `${window.location.origin}${fullPath}`;
  const href = `${targetHref}?redirectTo=${encodeURIComponent(currentPath)}`;
  locationHref(href);
}

/**
 * 更新用户token
 * @param headers
 */
function handleLoginToken(headers) {
  const token = headers[`${TOKEN_NAME}`];
  if (token) {
    setAuth(token);
    store.dispatch('updateAuth');
    // 设置监控用户ID
    const id = getUID();
    setMonitorUser(id);
  }
}

/**
 * 是否静默请求
 * @param options
 */
function isSilentRequest(options) {
  return options.silentRequest;
}

/**
 * 是否需要设置错误请求缓存
 * @param options
 * @returns {*}
 */
function shouldSetErrorRequestCache(options) {
  return !isSilentRequest(options) && !isExternalRequest(options);
}

/**
 * 设置错误请求缓存
 * @param options
 */
function setErrorRequestCache(options) {
  if (shouldSetErrorRequestCache(options)) {
    store.dispatch('updateErrorRequest', options);
  }
}

/**
 * 修正错误请求缓存
 * @param options
 */
function fixErrorRequestCache(options) {
  if (shouldSetErrorRequestCache(options)) {
    store.dispatch('updateErrorRequest', null);
  }
}

/**
 * 获取baseURL
 * @returns {string}
 */
function setupBaseUrl() {
  return `${process.env.VUE_APP_BACKEND_DOMAIN}${API_REQUEST_PREFIX}`;
}

/**
 * 判断能否携带认证
 * @param url
 * @returns {boolean}
 */
function canWithCredentials(url) {
  const BLACK_LIST = [
    'assets.youthmba.com',
    'res.youthmba.com',
  ];
  let res = true;
  BLACK_LIST.forEach((domain) => {
    if (url.indexOf(domain)) res = false;
  });
  return res;
}

/**
 * 过滤请求配置
 * @param config
 * @param deleteHeaders
 * @returns {*}
 */
function filterRequestConfig(config, deleteHeaders) {
  const { baseURL } = config;

  if (!canWithCredentials(baseURL)) {
    delete config.withCredentials;
  }

  if (deleteHeaders) {
    delete config.headers;
  }

  return config;
}

export default function request(options) {
  return new Promise((resolve, reject) => {
    // 非静默请求展示loading
    if (!isSilentRequest(options)) {
      Indicator.open();
    }

    const baseURL = setupBaseUrl();

    let config = {
      baseURL,
      timeout: 10000,
      headers: {
        [TOKEN_NAME]: getAuth(),
        [PLATFORM_API_KEY]: platformName(),
      },
      withCredentials: true,
    };

    const { multipartFormData, json, baseUrl } = options;

    if (baseUrl) {
      config = Object.assign({}, config, {
        baseURL: baseUrl,
      });
      // 过滤config
      config = filterRequestConfig(config, true);
    }

    // 上传文件处理
    if (multipartFormData) {
      config = Object.assign({}, config, {
        headers: { 'Content-Type': 'multipart/form-data' },
        transformRequest: [function transformRequest(data) {
          const formData = new FormData();
          Object.keys(data).forEach((key) => {
            formData.append(key, data[key]);
          });
          return formData;
        }],
      });
    }

    // 普通请求参数编码处理
    if (!multipartFormData && !json) {
      config = Object.assign({}, config, {
        paramsSerializer(params) {
          // 非json 则发送application/x-www-form-urlencoded 数据
          return stringifyData(params);
        },
        // 格式化'PUT', 'POST', and 'PATCH' 方法的数据
        transformRequest: [function transformRequest(data) {
          // 非json 则发送application/x-www-form-urlencoded 数据
          return stringifyData(data);
        }],
      });
    }

    const instance = axios.create(config);

    instance(options)
      .then(async (response) => {
        const { headers } = response;
        if (headers) {
          handleLoginToken(headers);
        }
        const res = response.data;
        // 没有response body
        if (!res) {
          monitor(response);
          return reject(response);
        }

        if (!isSilentRequest(options)) {
          // 隐藏loading
          Indicator.close();
        }

        const { code, msg, data } = res;

        // 修正错误请求缓存
        if (code === 302 || code === 401 || code === 200) {
          fixErrorRequestCache(options);
        }

        if (code === 302) {
          handleRedirect(msg, data.location);
          return false;
        }

        const { toastMessage = true } = options;
        if (toastMessage) {
          showMessage(msg); // 展示信息
        }

        if (code === 401) { // Unauthorized
          try {
            // Logout
            await store.dispatch('Logout').then(() => {
              redirectToLoginPage();
            });
            return false;
          } catch (e) {
            monitor(e);
            return reject(e);
          }
        }

        if (code === 200) { // Success
          // Share
          if (validator.hasProperty(res, 'pageShareInfo')) {
            const { pageShareInfo } = res;
            changePageShareInfo(pageShareInfo);
          }

          return resolve(data);
        }

        if (code !== 1000) {
          // 设置错误请求，以便再次请求
          setErrorRequestCache(options);
        }

        return reject(res);
      })
      .catch(async (error) => {
        const isSilent = isSilentRequest(options);
        // 非静默请求超时提示用户
        if (!isSilent) {
          const { message } = error;
          if (message === 'Network Error' || message.indexOf('timeout') > -1) {
            MessageBox.alert('网络错误，请稍后重试', '', {
              confirmButtonText: '知道了',
              closeOnClickModal: false,
            });
          }
        }
        monitor(error);
        // 设置错误请求，以便再次请求
        setErrorRequestCache(options);

        return reject(error);
      });
  });
}
